import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { List, X, User, SignOut, MapPin, SealCheck, Heart } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useLocation as useZyroLocation } from "../context/LocationContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { city, setCity, detect, detecting, cities } = useZyroLocation();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const links = [
    { to: "/hotels", label: "Hotels" },
    { to: "/rentals", label: "Rentals" },
    { to: "/wishlist", label: "Wishlist" },
    { to: "/help", label: "Help" },
  ];

  const handleLogout = async () => { await logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-40 glass border-b border-black/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          <img src="/zyro-logo.png" alt="Zyro" className="h-10 md:h-11 w-auto object-contain" />
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full hover:bg-stone transition-colors text-sm text-midnight" data-testid="location-btn">
              <MapPin size={16} weight="duotone" className="text-gold" />
              <span className="max-w-[100px] truncate">{city}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-xs tracking-widest uppercase text-neutral-500">Choose location</DropdownMenuLabel>
            <DropdownMenuItem onClick={detect} data-testid="detect-location">
              {detecting ? "Detecting..." : "📍 Use current location"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {cities.map((c) => (
              <DropdownMenuItem key={c} onClick={() => setCity(c)} data-testid={`loc-${c}`}>{c}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <nav className="hidden md:flex items-center gap-8 ml-auto mr-4">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) => `text-sm tracking-[0.15em] uppercase transition-colors duration-200 ${
                isActive ? "text-midnight" : "text-neutral-500 hover:text-midnight"}`}>{l.label}</NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 px-4 py-2 rounded-full border border-black/10 hover:bg-stone transition-colors">
                  <User size={18} weight="duotone" />
                  <span className="text-sm">{user.name.split(" ")[0]}</span>
                  {user.is_verified && <SealCheck size={16} weight="fill" className="text-emerald-600" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-neutral-500">
                  <div>{user.email}</div>
                  <div className="mt-1">{user.role} · {user.is_verified ? "✓ Verified" : "Not verified"}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/bookings" data-testid="menu-bookings">My Bookings</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/my-rentals" data-testid="menu-rentals">My Rentals</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/referral" data-testid="menu-referral">🎁 Invite &amp; earn ₹500</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/verification" data-testid="menu-verify">ID Verification</Link></DropdownMenuItem>
                {(user.role === "room_partner" || user.role === "hotel_partner" || user.role === "admin") && (
                  <DropdownMenuItem asChild><Link to="/payouts" data-testid="menu-payouts">Payouts</Link></DropdownMenuItem>
                )}
                {user.role === "admin" && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin">Admin Panel</Link></DropdownMenuItem>}
                {(user.role === "hotel_partner" || user.role === "admin") && (
                  <>
                    <DropdownMenuItem asChild><Link to="/partner/hotels" data-testid="menu-partner-hotels">Hotels</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/partner/bookings" data-testid="menu-partner-bookings">Manage Bookings</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/partner/reports" data-testid="menu-partner-reports">Income Reports</Link></DropdownMenuItem>
                  </>
                )}
                {(user.role === "room_partner" || user.role === "admin") && (
                  <DropdownMenuItem asChild><Link to="/partner/rooms" data-testid="menu-partner-rooms">Room Partner Panel</Link></DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout"><SignOut size={16} className="mr-2" /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="text-sm tracking-[0.15em] uppercase text-neutral-600 hover:text-midnight transition-colors">Sign In</Link>
              <Link to="/register" data-testid="nav-register-btn" className="px-5 py-2.5 rounded-full bg-midnight text-white text-sm tracking-wide hover:bg-midnight-hover transition-colors">Get Started</Link>
            </>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white/95 backdrop-blur-lg px-6 py-6 space-y-4">
          <div className="flex items-center gap-2 text-sm text-midnight"><MapPin size={16} weight="duotone" className="text-gold" /><span>{city}</span></div>
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm tracking-[0.15em] uppercase">{l.label}</Link>
          ))}
          {user ? (
            <>
              <Link to="/bookings" onClick={() => setOpen(false)} className="block text-sm">My Bookings</Link>
              <Link to="/my-rentals" onClick={() => setOpen(false)} className="block text-sm">My Rentals</Link>
              <Link to="/verification" onClick={() => setOpen(false)} className="block text-sm">ID Verification</Link>
              {(user.role === "room_partner" || user.role === "hotel_partner" || user.role === "admin") && <Link to="/payouts" onClick={() => setOpen(false)} className="block text-sm">Payouts</Link>}
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block text-sm">Admin</Link>}
              {(user.role === "hotel_partner" || user.role === "admin") && <Link to="/partner/hotels" onClick={() => setOpen(false)} className="block text-sm">Hotel Partner</Link>}
              {(user.role === "room_partner" || user.role === "admin") && <Link to="/partner/rooms" onClick={() => setOpen(false)} className="block text-sm">Room Partner</Link>}
              <button onClick={handleLogout} className="text-sm text-red-700">Logout</button>
            </>
          ) : (
            <div className="flex gap-3">
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm">Sign In</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-medium">Register</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
